import { useState } from "react";
import { CheckCircle2, AlertTriangle, ChevronDown } from "lucide-react";
import { ResultsTable, ResultsCards } from "./ResultsTable";
import { money, moneyExact, formatDate } from "@/lib/scoring";

const RoundCard = ({ round, index, showAll = false }) => {
  const [open, setOpen] = useState(false);
  const distributable = round.playerCount * 35 + round.carryIn - round.carryOut;
  const distributed = round.players.reduce((s, p) => s + p.totalWonCalc, 0);
  const payoutOk = Math.abs(distributed - distributable) < 0.005;
  const maxDots = Math.max(0, ...round.players.map((p) => p.dots));
  const roundBadges = (p) => {
    const b = [];
    if (maxDots > 0 && p.dots === maxDots) b.push("rdDots");
    if (p.parIn) b.push("parInKing");
    return b;
  };
  // Display only: older cards show players who finished positive;
  // everyone at zero or below is counted in the "others played" line.
  // The most recent round (showAll) lists everybody. Data untouched.
  const shownPlayers = showAll ? round.players : round.players.filter((p) => p.totalWon > 0);
  const hiddenCount = round.players.length - shownPlayers.length;
  return (
    <div className="overflow-hidden rounded-xl border border-cypress-gold/20 bg-cypress-green/30" data-testid={`round-card-${index}`}>
      <button
        type="button"
        data-testid={`round-card-toggle-${index}`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex w-full items-center gap-4 px-4 py-4 text-left sm:px-5"
      >
        {round.photo && (
          <img
            src={round.photo}
            alt={`Photo from ${round.day}, ${formatDate(round.date)}`}
            loading="lazy"
            className="h-16 w-16 shrink-0 rounded-lg border border-cypress-gold/20 object-cover sm:h-20 sm:w-20"
            data-testid={`round-card-photo-${index}`}
          />
        )}
        <div className="min-w-0 flex-1">
          <p className="font-bold tracking-tight sm:text-lg">
            {round.day}, {formatDate(round.date)}
          </p>
          <p className="mt-0.5 font-mono text-xs text-cypress-cream/50 sm:text-sm">
            {round.playerCount} players · {round.totalDots} dots · {moneyExact(round.valuePerDot)} per dot
          </p>
          {round.carryIn > 0 && (
            <p className="mt-1 font-mono text-xs text-cypress-gold">Carryover in: {money(round.carryIn)} added to Par In pot</p>
          )}
          {round.carryOut > 0 && (
            <p className="mt-1 font-mono text-xs text-cypress-gold">Par In unclaimed — {money(round.carryOut)} carries to the next round</p>
          )}
          <p
            className={`mt-1 flex items-center gap-1 font-mono text-xs ${payoutOk ? "text-emerald-400" : "font-bold text-red-400"}`}
            data-testid={`payout-check-${index}`}
          >
            {payoutOk ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
            Payout check: {money(distributed)} of {money(distributable)} distributed{payoutOk ? "" : " — CHECK THE NUMBERS"}
          </p>
        </div>
        <ChevronDown className={`h-5 w-5 shrink-0 text-cypress-gold/60 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-cypress-gold/15 px-4 py-4 sm:px-5" data-testid={`round-card-results-${index}`}>
          {round.headline && (
            <p
              className={`${round.teamBet ? "mb-1" : "mb-4"} border-l-2 border-cypress-gold pl-3 text-base font-semibold italic leading-snug text-cypress-gold sm:text-lg`}
              data-testid={`round-headline-${index}`}
            >
              {round.headline}
            </p>
          )}
          {round.teamBet && (
            <p
              className={`mb-4 pl-3 text-sm italic leading-snug text-cypress-cream/55 ${round.headline ? "" : "border-l-2 border-transparent"}`}
              data-testid={`round-teambet-${index}`}
            >
              {round.teamBet}
            </p>
          )}
          <div className="hidden md:block">
            <ResultsTable players={shownPlayers} testIdPrefix={`round-${index}`} badgesFor={roundBadges} gooseEggs />
          </div>
          <div className="md:hidden">
            <ResultsCards players={shownPlayers} testIdPrefix={`round-${index}`} badgesFor={roundBadges} gooseEggs />
          </div>
          {hiddenCount > 0 && (
            <p className="mt-3 text-center text-xs text-cypress-cream/40" data-testid={`round-others-${index}`}>
              {hiddenCount} {hiddenCount === 1 ? "other" : "others"} played.
            </p>
          )}
          <p className="mt-3 text-center text-xs text-cypress-cream/40" data-testid={`round-net-note-${index}`}>
            All figures are net. The $35 buy-in is already subtracted.
          </p>
          <p className="mt-1 text-center text-xs text-cypress-cream/40" data-testid={`round-rounding-note-${index}`}>
            Due to rounding, the dollar amounts can be off up or down about a buck.
          </p>
        </div>
      )}
    </div>
  );
};

export const RoundsSection = ({ rounds }) => {
  if (rounds.length === 0) {
    return (
      <div className="border-y border-cypress-gold/10 bg-cypress-green/20">
        <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-cypress-gold">Rounds</p>
          <div className="mt-4 flex items-center gap-4">
            <img
              src="/images/hero.jpg"
              alt="The boys with the cart by the water on Saturday"
              loading="lazy"
              className="h-20 w-20 rounded-lg border border-cypress-gold/20 object-cover"
            />
            <p className="text-sm text-cypress-cream/60" data-testid="rounds-empty-note">
              No rounds yet — the first one tees off Saturday. Full results land here as soon as the numbers are in.
            </p>
          </div>
        </div>
      </div>
    );
  }
  // Display only: the three most recent rounds get cards. Older rounds
  // stay in the data and still count toward season totals and records.
  const newestFirst = [...rounds].reverse().slice(0, 3);
  return (
    <div className="border-y border-cypress-gold/10 bg-cypress-green/20">
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 sm:py-10">
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-cypress-gold">Rounds</p>
        <h2 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">Every weekend, newest first</h2>
        <div className="mt-6 space-y-3" data-testid="rounds-list">
          {newestFirst.map((r, i) => (
            <RoundCard key={r.date} round={r} index={i + 1} showAll={i === 0} />
          ))}
        </div>
        {rounds.length > newestFirst.length && (
          <p className="mt-4 text-center text-xs text-cypress-cream/40" data-testid="rounds-earlier-note">
            Earlier rounds are still counted in the season totals.
          </p>
        )}
      </div>
    </div>
  );
};
